import {
  type EvidenceStatuses,
  type IntakeFormData as BackendIntakeFormData,
  IntakeStatus,
  type IntakeStatusValue,
  type IntakeSummary,
} from '../../../services/types/intake-types';
import type {
  Defendant,
  EvidenceStatus,
  EvidenceStatusesWithSubs,
  EvidenceStatusValue,
  GeneralNoteEntry,
  IntakeFormData,
} from './types';

// ---------- Frontend ← Backend (snake → camel) ----------
/**
 * Convert backend API format to frontend form format
 */
export const convertBackendToFrontend = (
  backendData: BackendIntakeFormData,
): IntakeFormData => {
  const normalizeEvidence = (
    input?: { status?: string; notes?: string } | null,
  ): EvidenceStatus => {
    const allowed: Record<string, EvidenceStatusValue> = {
      CONFIRMED: 'CONFIRMED',
      EXISTS_NOT_HAVE: 'EXISTS_NOT_HAVE',
      UNCERTAIN: 'UNCERTAIN',
      DOES_NOT_EXIST: 'DOES_NOT_EXIST',
    };
    const status =
      input?.status && allowed[input.status]
        ? allowed[input.status]
        : ('UNCERTAIN' as EvidenceStatusValue);
    return {
      status,
      notes: input?.notes ?? '',
    };
  };

  const hasSavedEvidenceStatuses = Boolean(
    backendData.evidence_statuses &&
      (backendData.evidence_statuses.photos?.notes !== undefined ||
        backendData.evidence_statuses.witnesses?.notes !== undefined ||
        backendData.evidence_statuses.police_report?.notes !== undefined ||
        backendData.evidence_statuses.ems?.notes !== undefined ||
        backendData.evidence_statuses.medical_treatment?.notes !== undefined),
  );

  // Build base evidence statuses
  const evidenceStatusesBase: Record<string, EvidenceStatus> = {
    // Read saved statuses only for notes persistence; we will recompute/merge in the dialog
    photos: normalizeEvidence(
      hasSavedEvidenceStatuses
        ? backendData.evidence_statuses?.photos
        : undefined,
    ),
    witnesses: normalizeEvidence(
      hasSavedEvidenceStatuses
        ? backendData.evidence_statuses?.witnesses
        : undefined,
    ),
    policeReport: normalizeEvidence(
      hasSavedEvidenceStatuses
        ? backendData.evidence_statuses?.police_report
        : undefined,
    ),
    ems: normalizeEvidence(
      hasSavedEvidenceStatuses ? backendData.evidence_statuses?.ems : undefined,
    ),
    medicalTreatment: normalizeEvidence(
      hasSavedEvidenceStatuses
        ? backendData.evidence_statuses?.medical_treatment
        : undefined,
    ),
    insurance: normalizeEvidence(undefined),
    // Keep legacy rows
    lostWages: normalizeEvidence(undefined),
    financialImpact: normalizeEvidence(undefined),
  };

  // Attach optional coverage sub-rows so the dialog can respect saved overrides
  if (backendData.evidence_statuses) {
    evidenceStatusesBase.first_party = normalizeEvidence(
      backendData.evidence_statuses.first_party,
    );
    evidenceStatusesBase.health = normalizeEvidence(
      backendData.evidence_statuses.health,
    );
    evidenceStatusesBase.third_party = normalizeEvidence(
      backendData.evidence_statuses.third_party,
    );
  }

  type NoteEntryBackend = {
    at: string;
    text: string;
    author_id?: string;
    author_name?: string;
  };

  const gnContainer = backendData as unknown as {
    general_notes_log?: NoteEntryBackend[];
  };
  const generalNotesLog: GeneralNoteEntry[] = Array.isArray(
    gnContainer.general_notes_log,
  )
    ? gnContainer.general_notes_log.map((n: NoteEntryBackend) => ({
        at: n.at,
        text: n.text,
        authorId: n.author_id,
        authorName: n.author_name,
      }))
    : [];

  return {
    // Keep the editor as a draft; do not prefill with legacy text
    generalNotes: '',
    generalNotesLog,
    generalNotesLegacy: backendData.general_notes || '',
    referralSource: {
      description: backendData.referral_source?.description ?? '',
    },
    leadSource: (backendData as { lead_source?: string }).lead_source ?? '',
    evidenceStatuses:
      evidenceStatusesBase as unknown as IntakeFormData['evidenceStatuses'],
    personalInfo: {
      fullName: backendData.personal_info.full_name,
      dob: backendData.personal_info.dob,
      ssnLast4: backendData.personal_info.ssn_last_4,
      phone: backendData.personal_info.phone,
      email: backendData.personal_info.email,
      address: backendData.personal_info.address,
      preferredContact: backendData.personal_info.preferred_contact,
      bestTimeToReach: backendData.personal_info.best_time_to_reach,
      preferredLanguage: backendData.personal_info.preferred_language || '',
      driversLicenseNumber: backendData.personal_info.drivers_license_number,
      driversLicenseState: backendData.personal_info.drivers_license_state,
      emergencyContact: {
        name: backendData.personal_info.emergency_contact.name,
        phone: backendData.personal_info.emergency_contact.phone,
        relationship: backendData.personal_info.emergency_contact.relationship,
      },
    },
    incidentInfo: {
      date: backendData.incident_info.date,
      time: backendData.incident_info.time,
      location: backendData.incident_info.location,
      state: backendData.incident_info.state,
      weather: backendData.incident_info.weather,
      fromTo: backendData.incident_info.from_to,
      type: backendData.incident_info.type,
      description: backendData.incident_info.description,
      presentComplaints: backendData.incident_info.present_complaints,
      ems: backendData.incident_info.ems,
      hospital: backendData.incident_info.hospital,
      currentlyTreating: backendData.incident_info.currently_treating,
      policeReport: backendData.incident_info.police_report,
      policeDept: backendData.incident_info.police_dept,
      policeReportNumber: backendData.incident_info.police_report_number,
      photos: backendData.incident_info.photos,
      witnesses: backendData.incident_info.witnesses,
      injuredBodyParts: backendData.incident_info.injured_body_parts ?? [],
      painLevel: backendData.incident_info.pain_level ?? null,
    },
    insurance: {
      defendants: backendData.insurance.defendants.map((defendant) => {
        const d = defendant as unknown as {
          name: string;
          address: string;
          drivers_license_number: string;
          drivers_license_state: string;
          drivers_license_plate: string;
          insurance_company_name?: string;
          policy_holder_name?: string;
          policy_number?: string;
          claim_number?: string;
          make_model_year?: string;
          carrier_v2_id?: string | null;
          carrier_division_id?: string | null;
        };
        const carrierV2 = d.carrier_v2_id ?? null;
        const carrierDiv = d.carrier_division_id ?? null;
        return {
          name: d.name,
          address: d.address,
          driversLicenseNumber: d.drivers_license_number,
          driversLicenseState: d.drivers_license_state,
          driversLicensePlate: d.drivers_license_plate,
          insuranceCompanyName: d.insurance_company_name ?? '',
          policyHolderName: d.policy_holder_name ?? '',
          policyNumber: d.policy_number ?? '',
          claimNumber: d.claim_number ?? '',
          makeModelYear: d.make_model_year ?? '',
          // Back-compat: populate both camelCase and snake_case in frontend state
          carrierV2Id: carrierV2,
          carrierDivisionId: carrierDiv,
          carrier_v2_id: carrierV2,
          carrier_division_id: carrierDiv,
        } as Defendant;
      }),
      pipInsurance: (() => {
        const p = backendData.insurance.pip_insurance as unknown as {
          company_name?: string;
          policy_holder_name?: string;
          policy_number?: string;
          claim_number?: string;
          make_model_year?: string;
          license_plate?: string;
          carrier_v2_id?: string | null;
          carrier_division_id?: string | null;
        };
        const pipV2 = p?.carrier_v2_id ?? null;
        const pipDiv = p?.carrier_division_id ?? null;
        return {
          companyName: p?.company_name ?? '',
          policyHolderName: p?.policy_holder_name ?? '',
          policyNumber: p?.policy_number ?? '',
          claimNumber: p?.claim_number ?? '',
          makeModelYear: p?.make_model_year ?? '',
          licensePlate: p?.license_plate ?? '',
          // Back-compat: expose both camelCase and snake_case keys
          carrierV2Id: pipV2,
          carrierDivisionId: pipDiv,
          carrier_v2_id: pipV2,
          carrier_division_id: pipDiv,
        } as IntakeFormData['insurance']['pipInsurance'];
      })(),
      healthProvider: backendData.insurance.health_provider,
      healthType: backendData.insurance.health_type,
      healthMemberID: backendData.insurance.health_member_id,
      healthGroupID: backendData.insurance.health_group_id,
    },
    medicalWageHistory: {
      treatments: backendData.medical_wage_history.treatments.map(
        (treatment: {
          provider: string;
          date_of_service: string;
          treatment_received: string;
          still_treating: boolean;
        }) => ({
          provider: treatment.provider,
          dateOfService: treatment.date_of_service,
          treatmentReceived: treatment.treatment_received,
          stillTreating: treatment.still_treating,
        }),
      ),
      hasLostWages: backendData.medical_wage_history.has_lost_wages,
      employer: backendData.medical_wage_history.employer,
      jobTitle: backendData.medical_wage_history.job_title,
      hourlyWage: backendData.medical_wage_history.hourly_wage,
      employmentDuration: backendData.medical_wage_history.employment_duration,
      priorMedicalHistory:
        backendData.medical_wage_history.prior_medical_history,
    },
    legalLifestyle: {
      priorLegalHistory: backendData.legal_lifestyle.prior_legal_history,
      lifestyleImpact: backendData.legal_lifestyle.lifestyle_impact,
      pendingBills: backendData.legal_lifestyle.pending_bills,
      outOfPocketExpenses: backendData.legal_lifestyle.out_of_pocket_expenses,
      otherNotes: backendData.legal_lifestyle.other_notes,
    },
    companions: backendData.companions
      ? backendData.companions.map((row) => ({
          clientName: row.client_name,
          details: row.details ?? null,
          createdIntakeId: row.created_intake_id ?? null,
        }))
      : [],
  };
};
// ---------- Frontend → Backend (camel → snake) ----------
/**
 * Convert frontend form format to backend API format
 */
export const convertFrontendToBackend = (
  frontendData: IntakeFormData,
  intakeStatuses?: {
    loeStatus: string;
    attorneyDecision: string;
    rejectionReason: string;
    hipaaStatus: string;
    declarationStatus: string;
    insuranceStatus: string;
  },
): BackendIntakeFormData => {
  // Build backend evidence_statuses with optional coverage sub-rows
  const subsFrontend =
    (frontendData.evidenceStatuses as unknown as EvidenceStatusesWithSubs) ||
    ({} as EvidenceStatusesWithSubs);

  const evidenceStatusesBackend: EvidenceStatuses = {
    photos: frontendData.evidenceStatuses.photos,
    witnesses: frontendData.evidenceStatuses.witnesses,
    police_report: frontendData.evidenceStatuses.policeReport,
    ems: frontendData.evidenceStatuses.ems,
    medical_treatment: frontendData.evidenceStatuses.medicalTreatment,
  };
  if (subsFrontend.first_party) {
    evidenceStatusesBackend.first_party = subsFrontend.first_party;
  }
  if (subsFrontend.health) {
    evidenceStatusesBackend.health = subsFrontend.health;
  }
  if (subsFrontend.third_party) {
    evidenceStatusesBackend.third_party = subsFrontend.third_party;
  }

  return {
    // Use provided status values or defaults
    loe_status: intakeStatuses?.loeStatus || 'Not Sent',
    attorney_decision: intakeStatuses?.attorneyDecision || 'Not Reviewed',
    rejection_reason: intakeStatuses?.rejectionReason || '',
    hipaa_status: intakeStatuses?.hipaaStatus || 'Not Requested',
    declaration_status: intakeStatuses?.declarationStatus || 'Not Received',
    insurance_status: intakeStatuses?.insuranceStatus || 'Not Received',
    // Persist the structured log; omit legacy field to avoid clobbering
    general_notes_log: (frontendData.generalNotesLog || []).map(
      (n: GeneralNoteEntry) => ({
        at: n.at,
        text: n.text,
        author_id: n.authorId,
        author_name: n.authorName,
      }),
    ),
    referral_source: {
      description: frontendData.referralSource.description || '',
    },
    lead_source: frontendData.leadSource || '',
    evidence_statuses: evidenceStatusesBackend,

    personal_info: {
      full_name: frontendData.personalInfo.fullName,
      dob: frontendData.personalInfo.dob,
      ssn_last_4: frontendData.personalInfo.ssnLast4,
      phone: frontendData.personalInfo.phone,
      email: frontendData.personalInfo.email,
      address: frontendData.personalInfo.address,
      preferred_contact: frontendData.personalInfo.preferredContact,
      best_time_to_reach: frontendData.personalInfo.bestTimeToReach,
      preferred_language: frontendData.personalInfo.preferredLanguage,
      drivers_license_number: frontendData.personalInfo.driversLicenseNumber,
      drivers_license_state: frontendData.personalInfo.driversLicenseState,
      emergency_contact: {
        name: frontendData.personalInfo.emergencyContact.name,
        phone: frontendData.personalInfo.emergencyContact.phone,
        relationship: frontendData.personalInfo.emergencyContact.relationship,
      },
    },
    incident_info: {
      date: frontendData.incidentInfo.date,
      time: frontendData.incidentInfo.time,
      location: frontendData.incidentInfo.location,
      state: frontendData.incidentInfo.state,
      weather: frontendData.incidentInfo.weather,
      from_to: frontendData.incidentInfo.fromTo,
      type: frontendData.incidentInfo.type,
      description: frontendData.incidentInfo.description,
      present_complaints: frontendData.incidentInfo.presentComplaints,
      ems: frontendData.incidentInfo.ems,
      hospital: frontendData.incidentInfo.hospital,
      currently_treating: frontendData.incidentInfo.currentlyTreating,
      police_report: frontendData.incidentInfo.policeReport,
      police_dept: frontendData.incidentInfo.policeDept,
      police_report_number: frontendData.incidentInfo.policeReportNumber,
      photos: frontendData.incidentInfo.photos,
      witnesses: frontendData.incidentInfo.witnesses,
      injured_body_parts: frontendData.incidentInfo.injuredBodyParts,
      pain_level: frontendData.incidentInfo.painLevel,
    },
    insurance: {
      defendants: frontendData.insurance.defendants.map((defendant) => ({
        name: defendant.name,
        address: defendant.address,
        drivers_license_number: defendant.driversLicenseNumber,
        drivers_license_state: defendant.driversLicenseState,
        drivers_license_plate: defendant.driversLicensePlate,
        insurance_company_name: defendant.insuranceCompanyName,
        policy_holder_name: defendant.policyHolderName,
        policy_number: defendant.policyNumber,
        claim_number: defendant.claimNumber,
        make_model_year: defendant.makeModelYear,
        carrier_v2_id: defendant.carrier_v2_id ?? null,
        carrier_division_id: defendant.carrier_division_id ?? null,
      })),
      pip_insurance: {
        company_name: frontendData.insurance.pipInsurance.companyName,
        policy_holder_name:
          frontendData.insurance.pipInsurance.policyHolderName,
        policy_number: frontendData.insurance.pipInsurance.policyNumber,
        claim_number: frontendData.insurance.pipInsurance.claimNumber,
        make_model_year: frontendData.insurance.pipInsurance.makeModelYear,
        license_plate: frontendData.insurance.pipInsurance.licensePlate,
        carrier_v2_id:
          frontendData.insurance.pipInsurance.carrier_v2_id ?? null,
        carrier_division_id:
          frontendData.insurance.pipInsurance.carrier_division_id ?? null,
      } as unknown as BackendIntakeFormData['insurance']['pip_insurance'],
      health_provider: frontendData.insurance.healthProvider,
      health_type: frontendData.insurance.healthType,
      health_member_id: frontendData.insurance.healthMemberID,
      health_group_id: frontendData.insurance.healthGroupID,
    },
    medical_wage_history: {
      treatments: frontendData.medicalWageHistory.treatments.map(
        (treatment) => ({
          provider: treatment.provider,
          date_of_service: treatment.dateOfService,
          treatment_received: treatment.treatmentReceived,
          still_treating: treatment.stillTreating,
        }),
      ),
      has_lost_wages: frontendData.medicalWageHistory.hasLostWages,
      employer: frontendData.medicalWageHistory.employer,
      job_title: frontendData.medicalWageHistory.jobTitle,
      hourly_wage: frontendData.medicalWageHistory.hourlyWage,
      employment_duration: frontendData.medicalWageHistory.employmentDuration,
      prior_medical_history:
        frontendData.medicalWageHistory.priorMedicalHistory,
    },
    legal_lifestyle: {
      prior_legal_history: frontendData.legalLifestyle.priorLegalHistory,
      lifestyle_impact: frontendData.legalLifestyle.lifestyleImpact,
      pending_bills: frontendData.legalLifestyle.pendingBills,
      out_of_pocket_expenses: frontendData.legalLifestyle.outOfPocketExpenses,
      other_notes: frontendData.legalLifestyle.otherNotes,
    },
    companions: frontendData.companions
      ? frontendData.companions.map((row) => ({
          client_name: row.clientName,
          details: row.details ?? null,
          created_intake_id: row.createdIntakeId ?? null,
        }))
      : undefined,
    schema_version: '1.3',
  };
};

export type IntakeSummaryFrontend = {
  caseSummary: string;
  greenFlags: string;
  yellowFlags: string;
  redFlags: string;
  recommendationDecision:
    | 'Likely Accept'
    | 'Potential Accept'
    | 'Uncertain'
    | 'Potential Decline'
    | 'Likely Decline'
    | '';
  recommendationReason: string;
  recommendationCounterpointHeading?: string | null;
  recommendationCounterpoint?: string | null;
};

const EMPTY_SUMMARY: IntakeSummaryFrontend = {
  caseSummary: '',
  greenFlags: '',
  yellowFlags: '',
  redFlags: '',
  recommendationDecision: '',
  recommendationReason: '',
  recommendationCounterpointHeading: null,
  recommendationCounterpoint: null,
};

export const summaryBackendToFrontend = (
  s: IntakeSummary | null | undefined,
): IntakeSummaryFrontend => {
  if (!s) return { ...EMPTY_SUMMARY };
  // Normalize partial/empty objects from backend into a safe complete payload
  const n = normalizeBackendSummary(s as Partial<IntakeSummary>);
  const decision = n.recommendation_decision;
  const allowed: Record<
    string,
    IntakeSummaryFrontend['recommendationDecision']
  > = {
    'Likely Accept': 'Likely Accept',
    'Potential Accept': 'Potential Accept',
    Uncertain: 'Uncertain',
    'Potential Decline': 'Potential Decline',
    'Likely Decline': 'Likely Decline',
  };
  return {
    caseSummary: n.case_summary,
    greenFlags: n.green_flags,
    yellowFlags: n.yellow_flags,
    redFlags: n.red_flags,
    recommendationDecision: allowed[decision] ?? '',
    recommendationReason: n.recommendation_reason,
    recommendationCounterpointHeading:
      n.recommendation_counterpoint_heading ?? null,
    recommendationCounterpoint: n.recommendation_counterpoint ?? null,
  };
};

export const summaryFrontendToBackend = (
  f: IntakeSummaryFrontend,
): IntakeSummary => ({
  case_summary: f.caseSummary,
  green_flags: f.greenFlags,
  yellow_flags: f.yellowFlags,
  red_flags: f.redFlags,
  recommendation_decision: f.recommendationDecision || '',
  recommendation_reason: f.recommendationReason,
  recommendation_counterpoint_heading:
    f.recommendationCounterpointHeading ?? null,
  recommendation_counterpoint: f.recommendationCounterpoint ?? null,
});

// Normalizes a partially provided backend-shaped IntakeSummary into a complete payload
export const normalizeBackendSummary = (
  s: Partial<IntakeSummary>,
): IntakeSummary => ({
  green_flags: s.green_flags ?? '',
  yellow_flags: s.yellow_flags ?? '',
  red_flags: s.red_flags ?? '',
  case_summary: s.case_summary ?? '',
  recommendation_decision: s.recommendation_decision ?? '',
  recommendation_reason: s.recommendation_reason ?? '',
  recommendation_counterpoint_heading:
    s.recommendation_counterpoint_heading ?? null,
  recommendation_counterpoint: s.recommendation_counterpoint ?? null,
});

/** Returns true if all required summary fields are populated. */
export const isSummaryComplete = (s: IntakeSummaryFrontend): boolean => {
  const nonEmpty = (v: string | null | undefined) => Boolean(v && v.trim());
  return (
    nonEmpty(s.caseSummary) &&
    nonEmpty(s.recommendationDecision) &&
    nonEmpty(s.recommendationReason) &&
    nonEmpty(s.greenFlags) &&
    nonEmpty(s.yellowFlags) &&
    nonEmpty(s.redFlags)
  );
};

// -----------------------
// Status mapping helpers
// -----------------------

export type FrontendIntakeStatuses = {
  loeStatus:
    | 'Not Sent'
    | 'Sent'
    | 'Signed'
    | 'Sent (Manual)'
    | 'Signed (Manual)';
  attorneyDecision:
    | 'Not Reviewed'
    | 'Sent for Review'
    | 'Attorney Approved'
    | 'Finch Rejection'
    | 'Attorney Rejection';
  rejectionReason: string;
  hipaaStatus: 'Not Requested' | 'Requested' | 'Received';
  declarationStatus: 'Not Received' | 'Received';
  insuranceStatus: 'Not Received' | 'Received';
};

export const mapBackendFormStatusesToFrontend = (
  formData: BackendIntakeFormData,
): FrontendIntakeStatuses => ({
  loeStatus: formData.loe_status as FrontendIntakeStatuses['loeStatus'],
  attorneyDecision:
    formData.attorney_decision as FrontendIntakeStatuses['attorneyDecision'],
  rejectionReason: formData.rejection_reason || '',
  hipaaStatus: formData.hipaa_status as FrontendIntakeStatuses['hipaaStatus'],
  declarationStatus:
    formData.declaration_status as FrontendIntakeStatuses['declarationStatus'],
  insuranceStatus:
    formData.insurance_status as FrontendIntakeStatuses['insuranceStatus'],
});

export const mapBackendIntakeStatusToFrontend = (
  backendStatus: IntakeStatusValue,
):
  | 'In Progress'
  | 'Chase'
  | 'Sent for Client Signature'
  | 'Signed'
  | 'Archived'
  | 'Rejected'
  | 'Lost'
  | 'Escalated' => {
  const statusMap: Record<IntakeStatusValue, string> = {
    [IntakeStatus.IN_PROGRESS]: 'In Progress',
    [IntakeStatus.CHASE]: 'Chase',
    [IntakeStatus.SENT_FOR_SIGNATURE]: 'Sent for Client Signature',
    [IntakeStatus.SIGNED]: 'Signed',
    [IntakeStatus.ARCHIVED]: 'Archived',
    [IntakeStatus.REJECTED]: 'Rejected',
    [IntakeStatus.LOST]: 'Lost',
    [IntakeStatus.ESCALATED]: 'Escalated',
  };
  return (statusMap[backendStatus] || 'In Progress') as
    | 'In Progress'
    | 'Chase'
    | 'Sent for Client Signature'
    | 'Signed'
    | 'Archived'
    | 'Rejected'
    | 'Lost'
    | 'Escalated';
};
